import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarCheck, Pill, Receipt, DownloadSimple, Heartbeat } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const STATUS_CLS = {
  scheduled: "bg-primary/10 text-primary border-primary/30", completed: "bg-success/10 text-success border-success/40",
  cancelled: "bg-muted text-muted-foreground border-border", no_show: "bg-destructive/10 text-destructive border-destructive/40",
  paid: "bg-success/10 text-success border-success/40", partial: "bg-warning/10 text-warning border-warning/40", unpaid: "bg-destructive/10 text-destructive border-destructive/40",
};
const Badge = ({ s }) => <span className={`inline-flex px-2 py-0.5 rounded-sm border text-xs font-medium capitalize ${STATUS_CLS[s] || "border-border"}`}>{String(s).replace("_", " ")}</span>;

async function downloadInvoice(inv) {
  try {
    const { data } = await api.get(`/clinic/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a");
    a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Could not download invoice"); }
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-card border border-border rounded-md">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Icon size={18} className="text-primary" /><h3 className="font-heading font-semibold">{title}</h3></div>
      {children}
    </div>
  );
}

export default function Portal() {
  const { money } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/clinic/portal").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="p-8"><div className="h-6 w-40 bg-muted rounded animate-pulse" /></div>;

  if (!d.linked) {
    return (
      <div data-testid="portal-page">
        <PageHeader title="My health portal" subtitle="Your prosthetics & orthotics care record" />
        <div className="p-6 md:p-8">
          <div className="h-60 flex flex-col items-center justify-center text-muted-foreground gap-3 border border-dashed border-border rounded-md">
            <Heartbeat size={30} />
            <p className="text-sm max-w-sm text-center">Your account isn't linked to a patient record yet. Please contact the clinic so they can connect your file.</p>
          </div>
        </div>
      </div>
    );
  }
  const p = d.patient;

  return (
    <div data-testid="portal-page">
      <PageHeader title={`Welcome, ${p.first_name || p.name}`} subtitle={`${p.patient_no}${d.outstanding > 0 ? " · Outstanding balance " + money(d.outstanding) : ""}`} />
      <div className="p-6 md:p-8 space-y-6">
        {d.next_appointment && (
          <div className="bg-primary/5 border border-primary/30 rounded-md p-5 flex items-center gap-4" data-testid="portal-next-appt">
            <span className="h-11 w-11 rounded-md bg-primary/10 text-primary flex items-center justify-center"><CalendarCheck size={22} weight="bold" /></span>
            <div><p className="text-xs uppercase tracking-wider text-primary font-semibold">Next appointment</p>
              <p className="text-sm mt-0.5">{fmtDate(d.next_appointment.date)} at {d.next_appointment.time} with {d.next_appointment.doctor_name}</p></div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          <Card icon={CalendarCheck} title="Appointments">
            {d.appointments.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No appointments.</p> : (
              <ul className="divide-y divide-border">
                {d.appointments.map((a) => (
                  <li key={a.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`portal-appt-${a.id}`}>
                    <div><p className="text-sm font-medium">{fmtDate(a.date)} · {a.time}</p><p className="text-xs text-muted-foreground">{a.doctor_name}{a.reason ? ` · ${a.reason}` : ""}</p></div>
                    <Badge s={a.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card icon={Pill} title="Prescriptions">
            {d.prescriptions.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No prescriptions.</p> : (
              <ul className="divide-y divide-border">
                {d.prescriptions.map((rx) => (
                  <li key={rx.id} className="px-5 py-3" data-testid={`portal-rx-${rx.id}`}>
                    <p className="text-sm font-medium">{rx.rx_no} <span className="text-muted-foreground font-normal">· {fmtDate(rx.created_at)}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">{rx.items.map((i) => i.name).join(", ")}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card icon={Receipt} title="Invoices">
          {d.invoices.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No invoices.</p> : (
            <ul className="divide-y divide-border">
              {d.invoices.map((inv) => (
                <li key={inv.id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`portal-inv-${inv.id}`}>
                  <div><p className="text-sm font-medium">{inv.invoice_no} <span className="text-muted-foreground font-normal">· {fmtDate(inv.created_at)}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">Total {money(inv.total)} · Due {money(inv.balance)}</p></div>
                  <div className="flex items-center gap-2">
                    <Badge s={inv.status} />
                    <Button size="sm" variant="ghost" onClick={() => downloadInvoice(inv)} data-testid={`portal-pdf-${inv.id}`} className="gap-1.5"><DownloadSimple size={14} /> PDF</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
